'use client';

import { useState } from 'react';

import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { useEventSearch } from '../hooks/useEventSearch';

/** Extract an event ticker from a pasted Kalshi URL or a raw ticker string. */
function extractTicker(raw: string): string {
    const trimmed = raw.trim();
    if (trimmed.startsWith('http')) {
        try {
            const segments = new URL(trimmed).pathname.split('/').filter(Boolean);
            const last = segments[segments.length - 1] ?? '';
            return last.toUpperCase();
        } catch {
            // fall through to raw handling
        }
    }
    return trimmed.toUpperCase();
}

interface EventSearchFieldProps {
    value: string;
    onChange: (eventTicker: string) => void;
    allowManualTicker?: boolean;
}

/**
 * Event picker: type to search, paste a Kalshi event URL, or paste a raw ticker.
 * Replaces the ~50-line search block duplicated across SymbolDetail / Watchlist /
 * AdvancedChart (now backed by `useEventSearch`).
 */
export function EventSearchField({ value, onChange, allowManualTicker = true }: EventSearchFieldProps) {
    const [open, setOpen] = useState(false);
    const search = useEventSearch({
        onSelect: (result) => {
            onChange(result.eventTicker);
            setOpen(false);
        },
    });

    return (
        <div className="relative">
            <Input
                value={search.query}
                placeholder={value ? value : 'Search markets or paste an event URL'}
                spellCheck={false}
                onChange={(e) => {
                    search.setQuery(e.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && search.results.length === 0 && allowManualTicker && search.query.trim()) {
                        e.preventDefault();
                        onChange(extractTicker(search.query));
                        search.reset();
                        setOpen(false);
                    } else {
                        search.onKeyDown(e);
                    }
                }}
            />

            {value && !search.query && (
                <p className="typ-body-x10 mt-1 text-text-x20">
                    Selected: <span className="font-mono text-text-x10">{value}</span>
                </p>
            )}

            {open && (search.isLoading || search.results.length > 0) && (
                <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-x30 border border-stroke-x30 bg-surface-x10 shadow-x20">
                    {search.isLoading && search.results.length === 0 && (
                        <div className="typ-body-x10 px-3 py-2 text-text-x20">Searching…</div>
                    )}
                    {search.results.map((result, index) => (
                        <button
                            key={result.eventTicker}
                            type="button"
                            className={cn(
                                'typ-body-x30 flex w-full items-center gap-2 px-3 py-2 text-left text-text-x10',
                                index === search.highlightedIndex ? 'bg-container-x30' : 'hover:bg-container-x30',
                            )}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => search.setHighlightedIndex(index)}
                            onClick={() => {
                                onChange(result.eventTicker);
                                search.reset();
                                setOpen(false);
                            }}
                        >
                            <span className="truncate font-medium">{result.eventTitle}</span>
                            <span className="ml-auto shrink-0 font-mono text-xs text-text-x30">
                                {result.eventTicker}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
